import React from 'react'
import { CustomTooltip, Modal, PrimaryButton, SelectBox, SelectBoxProps, TextBox } from 'components'

export type AmendableSelectBoxProps = {
  options: { text: string; value: string }[]
  value: string
  onSelect: (text: string, value: string) => void
  onAmend: (newValue: string) => void
  onBlurSelect?: () => void
  required?: boolean
  disabled?: boolean
  amendButtonText?: string
  amendModalTitle?: string
  amendModalLabel?: string
  amendModalPlaceholder?: string
  amendModalTooltip?: React.ReactNode | string
  amendModalError?: (value: string) => boolean
  amendModalErrorText?: (value: string) => string
  disableAmend?: boolean
  label?: string
  placeholder?: string
  error?: boolean
  helperText?: string
  className?: string
  id?: string
  testId?: string
  selectBoxProps?: SelectBoxProps
}

export const AmendableSelectBox: React.FC<AmendableSelectBoxProps> = (props: AmendableSelectBoxProps) => {
  const { options, value, onSelect, onAmend, onBlurSelect } = props

  const amendButtonText: string = props.amendButtonText ?? 'Add'
  const amendModalTitle: string = props.amendModalTitle ?? 'Add Option'
  const amendModalLabel: string = props.amendModalLabel ?? 'New Option'
  const amendModalPlaceholder: string = props.amendModalPlaceholder ?? 'Enter New Option'
  const disableAmend: boolean = props.disableAmend ?? false

  const disabled: boolean = props.disabled ?? false
  const label: string = props.label ?? ''
  const placeholder: string = props.placeholder ?? ''
  const error: boolean = props.error ?? false
  const helperText: string = props.helperText ?? ''

  const id: string = props.id ?? 'value'
  const className: string = props.className ?? 'w-full sm:w-64'
  const testid = props.testId ?? 'amendable-select-box'
  const selectBoxProps: SelectBoxProps | undefined = props.selectBoxProps

  const [amendModalOpen, setAmendModalOpen] = React.useState<boolean>(false)
  const [amendText, setAmendText] = React.useState<string>('')
  const [amendBlurred, setAmendBlurred] = React.useState<boolean>()

  const cancelAmend = (): void => {
    setAmendModalOpen(false)
    setAmendBlurred(false)
    setAmendText('')
  }
  const confirmAmend = (): void => {
    onAmend(amendText)
    setAmendBlurred(false)
    setAmendModalOpen(false)
    setAmendText('')
  }

  const duplicateError: boolean =
    options.find((item) => item.value.toUpperCase() === amendText.toUpperCase()) !== undefined

  const amendTextError: boolean =
    amendBlurred && props.amendModalError ? props.amendModalError(amendText) || duplicateError : duplicateError
  const amendTextHelperText: string | undefined = amendTextError
    ? duplicateError
      ? `Error: ${id.charAt(0).toUpperCase() + id.slice(1)} already exists`
      : props.amendModalErrorText
      ? props.amendModalErrorText(amendText)
      : ''
    : undefined

  return (
    <div className={className}>
      <div className={className} data-testid={testid}>
        <div className={`flex pb-4 flex-row items-center ${label ? 'justify-between' : 'justify-end'}`}>
          {label && (
            <label className={`text-lg font-semibold ${disabled ? 'text-body-lighter' : ''}`} htmlFor={id}>
              {label}
            </label>
          )}
          <div
            onClick={() => setAmendModalOpen(true)}
            style={disableAmend ? { pointerEvents: 'none', opacity: '0.2' } : {}}
          >
            <span className={`text-primary-tint underline cursor-pointer`}>{amendButtonText}</span>
          </div>
        </div>
        <SelectBox
          id={id}
          placeholder={placeholder}
          isLoading={false}
          onOpen={() => {}}
          value={value}
          onChange={onSelect}
          options={options}
          error={error}
          helperText={helperText}
          {...selectBoxProps}
          enableSearch={true}
          onBlur={onBlurSelect}
        />
      </div>
      <Modal open={amendModalOpen} onClose={cancelAmend} title={amendModalTitle}>
        <div className={`flex flex-col`}>
          {props.amendModalTooltip ? (
            <CustomTooltip value={props.amendModalTooltip}>
              <div className={`w-full sm:w-64`}>
                <TextBox
                  id={`new-${id}`}
                  value={amendText}
                  onChange={(value: string) => setAmendText(value)}
                  outlined={true}
                  label={amendModalLabel}
                  placeholder={amendModalPlaceholder}
                  error={amendTextError}
                  helperText={amendTextHelperText}
                  onBlur={() => setAmendBlurred(true)}
                />
              </div>
            </CustomTooltip>
          ) : (
            <div className={`w-full sm:w-64`}>
              <TextBox
                id={`new-${id}`}
                value={amendText}
                onChange={(value: string) => setAmendText(value)}
                outlined={true}
                label={amendModalLabel}
                placeholder={amendModalPlaceholder}
                error={amendTextError}
                helperText={amendTextHelperText}
                onBlur={() => setAmendBlurred(true)}
              />
            </div>
          )}
          <div className={`flex justify-center flex-row gap-x-7 mb-6 mt-10`}>
            <PrimaryButton outlined={true} buttonText={`Cancel`} onClick={cancelAmend} />
            <PrimaryButton
              outlined={false}
              buttonText={`Ok`}
              onClick={confirmAmend}
              disabled={amendText === '' || duplicateError || props.amendModalError?.(amendText)}
            />
          </div>
        </div>
      </Modal>
    </div>
  )
}
